import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Award, ChevronDown, ChevronRight, X } from 'lucide-react'
import { MIN_SEARCH_LENGTH, useDebouncedValue } from '../../roster/index.js'
import type { DemoOpener, DemoPeople, DemoPeopleFacility, DemoPerson, DemoSessionRole, OpenDemoSessionInput } from '../index.js'
import { Button, TIER_HEX, TIER_LABELS, Skeleton, type WorkerTier } from '../../core/index.js'
import { AdminIcon, DemoRoleIcon, FacilityAdministratorIcon, OperatorIcon, RootAdminIcon, WorkerIcon } from '../../core/index.js'

const ROLE_ICON: Record<DemoSessionRole, typeof OperatorIcon> = { operator: OperatorIcon, facility_administrator: FacilityAdministratorIcon, worker: WorkerIcon }

const OPENER_ICON: Record<DemoOpener['kind'], typeof OperatorIcon> = { root: RootAdminIcon, admin: AdminIcon, operator: OperatorIcon, facility_administrator: FacilityAdministratorIcon }
const OPENER_WORD: Record<DemoOpener['kind'], string> = { root: 'Root admin', admin: 'Admin', operator: 'Operator', facility_administrator: 'Facility administrator' }

/** The round button that opens the chooser, wherever it sits: a client row, a header, the demo banner. */
export function DemoRoleButton({ label, onClick, tone = 'light', showLabel = false, disabled = false }: { label: string; onClick: () => void; tone?: 'light' | 'dark' | 'banner'; showLabel?: boolean; disabled?: boolean }) {
  const tones = {
    light: 'border-marine/30 text-marine hover:bg-marine-lt/40',
    dark: 'border-white/40 text-white hover:bg-white/10',
    banner: 'border-ink/30 text-ink hover:bg-white/50',
  } as const
  const handleClick = (event: MouseEvent) => {
    event.stopPropagation()
    onClick()
  }
  // With its label it is a button like any other; on its own it stays an icon in the tone of what it sits on.
  if (showLabel) {
    return (
      <Button icon={DemoRoleIcon} size="sm" title={label} aria-label={label} disabled={disabled} onClick={handleClick} className="align-middle">
        {label}
      </Button>
    )
  }
  return (
    <button type="button" title={label} aria-label={label} disabled={disabled} onClick={handleClick} className={`inline-flex items-center gap-1 rounded border px-1.5 py-1 text-[11px] font-bold align-middle disabled:opacity-50 ${tones[tone]}`}>
      <DemoRoleIcon className="w-3.5 h-3.5" />
    </button>
  )
}

/** The first choice in a demo session's chooser: the account that opened it. Choosing it ends the demo session rather than switching it. */
function OpenerRow({ opener, onReturn, disabled }: { opener: DemoOpener; onReturn: () => void; disabled: boolean }) {
  const Icon = OPENER_ICON[opener.kind]
  return (
    <div>
      <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid mb-1">Your account</div>
      <button type="button" disabled={disabled} onClick={onReturn} className="w-full flex items-center gap-2 border border-coral/40 bg-coral-lt/40 px-3 py-2 text-left text-xs text-ink hover:bg-coral-lt disabled:opacity-60">
        <Icon className="w-4 h-4 text-coral" />
        <span className="flex-1">
          <span className="font-bold">{opener.label}</span> <span className="text-ink-mid">· {OPENER_WORD[opener.kind]}</span>
        </span>
        <span className="text-[10px] font-bold text-ink-mid">Ends this demo session</span>
      </button>
    </div>
  )
}

interface DemoPeopleModalProps {
  title: string
  /** One line under the title saying what choosing does: a new tab, or this tab changes. */
  subtitle: string
  people: DemoPeople | undefined
  /**
   * One facility's workers, asked for when that facility is opened in the
   * chooser. A large client's workers run to thousands, so the chooser's
   * first read (`people`) lists the facilities instead of everyone in them.
   */
  loadWorkers: (facilityId: string) => Promise<DemoPeople>
  /** The workers, at any facility within reach, whose name or id holds the text: what the box asks once two letters are typed, since the workers themselves are not loaded. */
  searchWorkers: (query: string) => Promise<DemoPeople>
  /** Tells one chooser's cached workers from another's: the client for an admin, the session otherwise. */
  workersKey: string
  isLoading: boolean
  loadError: string | null
  /** The person this tab already is, marked and not offered again. */
  current?: { role: DemoSessionRole; subject: string } | null
  onChoose: (input: OpenDemoSessionInput) => void
  /** For a demo session: ends it and goes back to the account that opened it (`people.opener`). */
  onReturnToOpener?: () => void
  /** True while a choice is being opened. */
  pending: boolean
  error: string | null
  onClose: () => void
}

function matches(person: DemoPerson, groupLabel: string, query: string): boolean {
  return query === '' || `${person.label} ${groupLabel}`.toLowerCase().includes(query)
}

/** How many rows of one list are drawn: a chooser is for finding someone, and hundreds of rows are slower to draw than to search. */
const ROWS_SHOWN = 12
/** The API searches workers from three letters, as every worker search does. */
const MIN_WORKER_SEARCH = MIN_SEARCH_LENGTH

function MoreNote({ shown, of, what }: { shown: number; of: number; what: string }) {
  return of > shown ? (
    <div className="px-3 py-1.5 text-[10px] text-ink-mid">
      Showing {shown} of {of.toLocaleString()} {what}. Type above to narrow them.
    </div>
  ) : null
}

interface PersonRowsProps {
  people: readonly DemoPerson[]
  current: { role: DemoSessionRole; subject: string } | null
  pending: boolean
  onChoose: (input: OpenDemoSessionInput) => void
}

/** Gold first: a demo usually wants to show a tier, so a facility's workers are listed by it, then by name. */
const TIER_ORDER: Record<WorkerTier, number> = { gold: 0, silver: 1, bronze: 2, probationary: 3 }

function byTier(people: readonly DemoPerson[]): DemoPerson[] {
  return [...people].sort((a, b) => (a.tier && b.tier ? TIER_ORDER[a.tier] - TIER_ORDER[b.tier] : 0) || a.label.localeCompare(b.label))
}

/** A worker's tier as a medal in the tier's colour and its name, so the chooser can be scanned by tier. */
function TierBadge({ tier }: { tier: WorkerTier }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-bold" style={{ borderColor: TIER_HEX[tier], color: TIER_HEX[tier] }}>
      <Award aria-hidden="true" className="h-3 w-3" />
      {TIER_LABELS[tier]}
    </span>
  )
}

function PersonRows({ people, current, pending, onChoose }: PersonRowsProps) {
  return (
    <>
      {byTier(people).map((person) => {
        const Icon = ROLE_ICON[person.role]
        const isCurrent = current !== null && current.role === person.role && current.subject === person.subject
        return (
          <li key={`${person.role}:${person.subject}`}>
            <button
              type="button"
              disabled={pending || isCurrent}
              onClick={() => onChoose({ as: person.role, subject: person.subject })}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-ink hover:bg-marine-lt/30 disabled:hover:bg-transparent disabled:opacity-60"
            >
              <Icon className="w-4 h-4 text-marine" />
              <span className="flex-1 font-bold">{person.label}</span>
              {person.tier && <TierBadge tier={person.tier} />}
              {isCurrent && <span className="text-[10px] font-bold text-ink-mid">This session</span>}
            </button>
          </li>
        )
      })}
    </>
  )
}

interface FacilityWorkersProps extends Omit<PersonRowsProps, 'people'> {
  facility: DemoPeopleFacility
  loadWorkers: (facilityId: string) => Promise<DemoPeople>
  workersKey: string
}

/** One facility in the chooser: a row that, opened, loads and lists that facility's workers - and only then. */
function FacilityWorkers({ facility, loadWorkers, workersKey, current, pending, onChoose }: FacilityWorkersProps) {
  const [isOpen, setIsOpen] = useState(false)
  const workersQuery = useQuery({ queryKey: ['demo-people-workers', workersKey, facility.facilityId], queryFn: () => loadWorkers(facility.facilityId), enabled: isOpen })
  const workers = workersQuery.data?.groups.flatMap((group) => group.people) ?? []
  const count = facility.workerCount === null ? null : `${facility.workerCount.toLocaleString()} ${facility.workerCount === 1 ? 'worker' : 'workers'}`
  return (
    <li>
      <button type="button" aria-expanded={isOpen} onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-ink hover:bg-marine-lt/30">
        {isOpen ? <ChevronDown aria-hidden="true" className="w-3.5 h-3.5 text-ink-mid" /> : <ChevronRight aria-hidden="true" className="w-3.5 h-3.5 text-ink-mid" />}
        <span className="flex-1 font-bold">{facility.name}</span>
        {count && <span className="text-[10px] text-ink-mid">{count}</span>}
      </button>
      {isOpen && (
        <ul className="border-t border-marine/10 bg-marine-lt/20 divide-y divide-marine/10 pl-5">
          {workersQuery.isPending ? (
            <li className="px-3 py-2 text-[11px] text-ink-mid">Loading workers…</li>
          ) : workersQuery.isError ? (
            <li className="px-3 py-2 text-[11px] text-coral">Could not load this facility's workers.</li>
          ) : workers.length === 0 ? (
            <li className="px-3 py-2 text-[11px] text-ink-mid">No workers at this facility.</li>
          ) : (
            <PersonRows people={workers} current={current} pending={pending} onChoose={onChoose} />
          )}
        </ul>
      )}
    </li>
  )
}

/**
 * The chooser for a demo client's people, front and centre: operators,
 * facility administrators and workers by facility, each with their role's
 * piece, with a box to narrow a long list. The list is the server's - it
 * only ever offers who the caller may open - so this draws it and reports
 * the choice. Escape or the backdrop closes it.
 *
 * Built for a client of any size: it opens on the staff and the names of
 * the facilities, draws a dozen rows of each list (the box finds the
 * rest), and reads a facility's workers when that facility is opened.
 */
export function DemoPeopleModal({ title, subtitle, people, loadWorkers, searchWorkers, workersKey, isLoading, loadError, current = null, onChoose, onReturnToOpener, pending, error, onClose }: DemoPeopleModalProps) {
  const [query, setQuery] = useState('')
  useEffect(() => {
    function closeOnEscape(event: KeyboardEvent): void {
      if (event.key === 'Escape' && !pending) {
        onClose()
      }
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [onClose, pending])

  const needle = query.trim().toLowerCase()
  const groups = useMemo(
    () => (people?.groups ?? []).map((group) => ({ ...group, people: group.people.filter((person) => matches(person, group.label, needle)) })).filter((group) => group.people.length > 0),
    [people, needle],
  )
  const facilities = useMemo(() => (people?.facilities ?? []).filter((facility) => needle === '' || facility.name.toLowerCase().includes(needle)), [people, needle])
  // Workers are not in `people`, so a name typed here is looked for by the API - once it is long enough to mean someone, and has stopped changing.
  const workerQuery = useDebouncedValue(needle)
  const canSearchWorkers = people?.facilities !== undefined && workerQuery.length >= MIN_WORKER_SEARCH
  const foundQuery = useQuery({ queryKey: ['demo-people-search', workersKey, workerQuery], queryFn: () => searchWorkers(workerQuery), enabled: canSearchWorkers })
  const foundGroups = canSearchWorkers && workerQuery === needle ? (foundQuery.data?.groups ?? []) : []
  const isFindingWorkers = people?.facilities !== undefined && needle.length >= MIN_WORKER_SEARCH && (workerQuery !== needle || foundQuery.isPending)
  const total = (people?.groups ?? []).reduce((sum, group) => sum + group.people.length, 0) + (people?.facilities?.length ?? 0)

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/60 flex items-center justify-center p-4 text-left font-normal normal-case tracking-normal"
      onClick={(event) => {
        event.stopPropagation()
        if (!pending) {
          onClose()
        }
      }}
    >
      <div role="dialog" aria-modal="true" aria-label={title} className="bg-white w-full max-w-lg max-h-[80vh] flex flex-col shadow-lg" onClick={(event) => event.stopPropagation()}>
        <div className="px-5 py-4 border-b border-marine/15 flex items-start justify-between gap-3">
          <div>
            <div className="text-[9px] font-bold uppercase tracking-[2.5px] text-coral">Demo client</div>
            <h2 className="text-base font-black text-ink">{title}</h2>
            <p className="text-[11px] text-ink-mid leading-snug mt-0.5">{subtitle}</p>
          </div>
          <button type="button" onClick={onClose} disabled={pending} aria-label="Close" className="text-ink-mid hover:text-ink disabled:opacity-40">
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>

        {total > 8 && (
          <div className="px-5 pt-3">
            <input
              type="search"
              value={query}
              autoFocus
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Find a person or a facility by name"
              aria-label="Find a person or a facility by name"
              className="w-full border border-marine/30 bg-white px-2 py-1.5 text-xs text-ink"
            />
          </div>
        )}

        <div className="flex-1 overflow-auto px-5 py-3">
          {isLoading ? (
            <Skeleton label="Loading people" />
          ) : loadError ? (
            <div className="text-xs text-coral">{loadError}</div>
          ) : total === 0 && !people?.opener ? (
            <div className="text-xs text-ink-mid">There is nobody to open as yet. Add a facility and its people first.</div>
          ) : groups.length === 0 && facilities.length === 0 && foundGroups.length === 0 && needle !== '' && !isFindingWorkers ? (
            <div className="text-xs text-ink-mid">Nobody matches “{query.trim()}”.</div>
          ) : (
            <div className="flex flex-col gap-3">
              {people?.opener && onReturnToOpener && (
                <OpenerRow opener={people.opener} onReturn={onReturnToOpener} disabled={pending} />
              )}
              {groups.map((group) => (
                <div key={group.label}>
                  <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid mb-1">{group.label}</div>
                  <ul className="border border-marine/15 divide-y divide-marine/10">
                    <PersonRows people={group.people.slice(0, ROWS_SHOWN)} current={current} pending={pending} onChoose={onChoose} />
                  </ul>
                  <MoreNote shown={ROWS_SHOWN} of={group.people.length} what="people" />
                </div>
              ))}
              {isFindingWorkers && <div className="text-[11px] text-ink-mid">Looking for workers…</div>}
              {foundQuery.isError && canSearchWorkers && <div className="text-[11px] text-coral">Could not search the workers.</div>}
              {foundGroups.map((group) => (
                <div key={`found:${group.label}`}>
                  <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid mb-1">{group.label}</div>
                  <ul className="border border-marine/15 divide-y divide-marine/10">
                    <PersonRows people={group.people} current={current} pending={pending} onChoose={onChoose} />
                  </ul>
                </div>
              ))}
              {facilities.length > 0 && (
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid mb-1">Workers, by facility</div>
                  <ul className="border border-marine/15 divide-y divide-marine/10">
                    {facilities.slice(0, ROWS_SHOWN).map((facility) => (
                      <FacilityWorkers key={facility.facilityId} facility={facility} loadWorkers={loadWorkers} workersKey={workersKey} current={current} pending={pending} onChoose={onChoose} />
                    ))}
                  </ul>
                  <MoreNote shown={ROWS_SHOWN} of={facilities.length} what="facilities" />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-marine/15 flex items-center justify-between gap-3 text-[11px]">
          <span className={error ? 'text-coral' : 'text-ink-mid'}>{error ?? (pending ? 'Opening…' : 'For an hour, without a password.')}</span>
          <button type="button" onClick={onClose} disabled={pending} className="font-bold text-ink-mid hover:text-ink disabled:opacity-40">
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
